import React, { useEffect, useState } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "src/components/common";
import { toast, ToastContainer } from "react-toastify";
import { ActionMenu } from "src/components/ui";
import { formatDateDmy } from "src/utils";
import { VendorTabs } from "src/components/Requests/VendorTab";
import {
	useAppDispatch,
	useAppSelector,
	setArtisanRequests,
	setLoadingArtisanRequests,
	setArtisanRequestError,
	setSearchField,
	useLazyGetAllArtisanRequestsQuery,
	useValidateArtisanRequestMutation,
} from "src/store";
import type { RequestTypes as StoreRequestTypes } from "src/store/features/requestSlice";
import { Input } from "src/components/inputs";
import searchIcon from "src/assets/images/input/searchIcon.svg";

interface Props {
	handleChange: (e: any) => void;
}

interface TransactionDetails {
	debited_for: string;
	account_number: string;
	status: string;
	bank_name: string;
	artisan_id: number;
}

interface RequestTypes {
	transaction_details: TransactionDetails;
	debited_for: string;
	amount: string;
	created_at: string;
	prev_balance: string;
	new_balance: string;
	transaction_id: string;
}

export const RhsHeading: React.FC<Props> = ({ handleChange }) => (
	<Input
		icon={<img src={searchIcon} alt='searchIcon' />}
		type='search'
		placeholder='Search'
		handleChange={handleChange}
	/>
);

const VendorWithdrawalRequest = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	let [searchParams, setSearchParams] = useSearchParams();
	const [allowRowClick, setAllowRowClick] = useState(true);

	const { artisanRequests, loadingArtisanRequests, searchField } =
		useAppSelector((state) => state.request);
	const [getAllArtisanRequests] = useLazyGetAllArtisanRequestsQuery();
	const [validateArtisanRequest] = useValidateArtisanRequestMutation();

	const handleChange = (e: any) => {
		dispatch(setSearchField(e.target.value));
	};

	useEffect(() => {
		document.title = "Request Page";
	}, []);

	useEffect(() => {
		const fetchArtisanRequests = async () => {
			dispatch(setLoadingArtisanRequests(true));
			dispatch(setArtisanRequestError(null));

			try {
				const result = await getAllArtisanRequests({page: 1, limit: 1}).unwrap();
				dispatch(
					setArtisanRequests(result.payload?.data?.pending_transaction || [])
				);
			} catch (error: any) {
				const errorMessage =
					error?.data?.error?.message || "Failed to fetch artisan requests";
				dispatch(setArtisanRequestError(errorMessage));
				console.error(error);
			} finally {
				dispatch(setLoadingArtisanRequests(false));
			}
		};

		fetchArtisanRequests();
	}, [dispatch, getAllArtisanRequests]);

	const filteredData = artisanRequests.filter((data) => {
		const account_name = data.debited_for ? data.debited_for.toLowerCase() : "";
		return account_name.includes(searchField.toLowerCase());
	});

	const handleNavigate = (row: { [x: string]: any }) => {
		navigate(`/vendors/${row?.transaction_details?.artisan_id}`);
	};

	const handleValidation = async (row: any, value: string) => {
		try {
			const result = await validateArtisanRequest({
				transaction_id: row?.transaction_id,
				action: value,
			}).unwrap();
			toast.success(result?.message || "Request processed successfully");
		} catch (error: any) {
			const errorMessage =
				error?.data?.error?.message || "Failed to process request";
			toast.error(errorMessage);
		}
	};

	const RequestTableHeaders = [
		{
			title: "Account Name",
			render: (row: RequestTypes) => `${row.transaction_details.debited_for}`,
		},
		{
			title: "Account No",
			render: (row: RequestTypes) =>
				`${row.transaction_details.account_number}`,
		},
		{
			title: "Amount",
			render: (row: RequestTypes) => `${row.amount}`,
		},
		{
			title: "Date",
			render: (row: RequestTypes) => formatDateDmy(row.created_at),
		},
		{
			title: "Status",
			render: (row: RequestTypes) => (
				<p
					className={`capitalize ${
						row.transaction_details.status === "pending"
							? "text-[#55C4F1]"
							: "text-[#7607BD]"
					}`}
				>
					{row.transaction_details.status === "pending"
						? "active"
						: row.transaction_details.status}
				</p>
			),
		},
		{
			title: "Previous Balance",
			render: (row: RequestTypes) => `${row.prev_balance}`,
		},
		{
			title: "New Balance",
			render: (row: RequestTypes) => `${row.new_balance}`,
		},
		{
			title: "--",
			render: (row: UsersListTypes) => (
				<ActionMenu
					setAllowRowClick={(bool: boolean) => {
						setAllowRowClick(bool);
					}}
					actions={[
						{
							title: <p className=' text-blueX'>Approve</p>,
							onClick: () => {
								handleValidation(row, "approve");
							},
						},
						{
							title: <p className=' text-redX'>Reject</p>,
							onClick: () => {
								handleValidation(row, "reject");
							},
						},
					]}
				/>
			),
		},
	];

	return (
		<div>
			<DashboardLayout
				pageTitle='Vendor request withdrawal '
				rhsHeading={<RhsHeading handleChange={handleChange} />}
			>
				<ToastContainer />
				<div>
					{loadingArtisanRequests ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								marginTop: "100px",
							}}
						>
							<Loader>loading...</Loader>{" "}
						</div>
					) : filteredData.length > 0 ? (
						<VendorTabs
							rows={artisanRequests}
							BookingsTableHeaders={RequestTableHeaders}
							allowRowClick={allowRowClick}
							title={
								<p className='count'>
									{filteredData.length > 1 ? filteredData.length : 0} Dispute
								</p>
							}
							onRowClick={handleNavigate}
							searchParams={searchParams}
							setSearchParams={setSearchParams}
						/>
					) : (
						<p className='table-entry-status'>No Data Found</p>
					)}
				</div>
			</DashboardLayout>
		</div>
	);
};

export default VendorWithdrawalRequest;
